import { useState } from 'react'
import { WorkflowRestartResponse } from '@/console/api/types'
import { Button } from '@/console/ui/Button'
import { Callout } from '@/console/ui/Callout'
import { ConfirmDialog } from '@/console/ui/ConfirmDialog'
import { DataTable } from '@/console/ui/DataTable'
import { Field } from '@/console/ui/Field'
import { Note } from '@/console/ui/Note'
import { SectionHeading } from '@/console/ui/SectionHeading'
import { SourceBadge } from '@/console/ui/SourceBadge'
import { Stack } from '@/console/ui/Stack'
import { Tag } from '@/console/ui/Tag'
import { RestartAsk, RestartCall, WorkflowRestartController } from './useWorkflowRestart'
import {
  MAX_RESTART_TARGETS,
  MAX_RESUME_ROUNDS,
  RESTART_WORKFLOW_TYPES,
  RUNBOOK_URL,
  START_PROBLEM_MESSAGE,
  STATUS_COUNT_LABEL,
  isClosedAfterWindow,
  nextStepsOf,
  parseResumeTargets,
  resumableTargetsOf,
  startProblemOf,
  targetKey,
} from './workflowRestart'
import styles from './WorkflowRestart.module.css'

const typeLabelOf = (value: string): string => RESTART_WORKFLOW_TYPES.find((t) => t.value === value)?.label ?? value

const StartForm = ({ c }: { c: WorkflowRestartController }): JSX.Element => {
  const [workflowType, setWorkflowType] = useState<string>(RESTART_WORKFLOW_TYPES[0].value)
  const [workflowIdPrefix, setWorkflowIdPrefix] = useState('')
  const [rolloutLowered, setRolloutLowered] = useState(false)

  const input = { workflowType, workflowIdPrefix }
  const problem = startProblemOf(input)
  const showProblem = problem === 'INVALID_PREFIX'

  return (
    <Stack>
      <div className={styles.row}>
        <Field label="워크플로 타입" width="0 0 220px">
          <select value={workflowType} disabled={c.submitting} onChange={(e) => setWorkflowType(e.target.value)}>
            {RESTART_WORKFLOW_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label} · {t.value}
              </option>
            ))}
            <option value="">타입 조건 없음 (접두만)</option>
          </select>
        </Field>
        <Field label="workflowId 접두 (선택)" width="1 1 280px">
          <input
            type="text"
            placeholder="FOPT-<그룹키>-1"
            spellCheck={false}
            value={workflowIdPrefix}
            disabled={c.submitting}
            onChange={(e) => setWorkflowIdPrefix(e.target.value)}
          />
        </Field>
      </div>
      {showProblem ? <Callout tone="crit">{START_PROBLEM_MESSAGE[problem]}</Callout> : null}
      {problem === 'NO_CONDITION' ? <Callout tone="warn">{START_PROBLEM_MESSAGE.NO_CONDITION}</Callout> : null}
      <label className={styles.check}>
        <input type="checkbox" checked={rolloutLowered} disabled={c.submitting} onChange={(e) => setRolloutLowered(e.target.checked)} />
        <span>feature.plusOrderPreallocation.rollout 비율을 먼저 내렸고 반영 시각을 기록했다</span>
      </label>
      <div>
        <Button disabled={problem !== null || !rolloutLowered || c.submitting} onClick={() => c.ask({ kind: 'start', input })}>
          {c.submitting ? 'restart 중…' : 'restart'}
        </Button>
      </div>
    </Stack>
  )
}

const ResumeForm = ({ c }: { c: WorkflowRestartController }): JSX.Element => {
  const [raw, setRaw] = useState('')
  const parsed = parseResumeTargets(raw)
  const canSubmit = parsed.targets.length > 0 && parsed.invalid.length === 0 && parsed.targets.length <= MAX_RESTART_TARGETS && !c.submitting

  return (
    <Stack>
      <Field label="workflowId:runId (줄바꿈 구분, [WORKFLOW-RESTART] 로그의 pending·restartFailed 값)">
        <textarea rows={3} spellCheck={false} value={raw} disabled={c.submitting} onChange={(e) => setRaw(e.target.value)} />
      </Field>
      <div className={styles.meta}>
        유효 {parsed.targets.length}건 · 최대 {MAX_RESTART_TARGETS}건
      </div>
      {parsed.invalid.length > 0 ? <Callout tone="crit">workflowId:runId 형식이 아닌 값이 있어요: {parsed.invalid.join(', ')}</Callout> : null}
      <div>
        <Button variant="line" disabled={!canSubmit} onClick={() => c.ask({ kind: 'resume', targets: parsed.targets })}>
          이어받기 ({parsed.targets.length}건)
        </Button>
      </div>
    </Stack>
  )
}

const Summary = ({ response }: { response: WorkflowRestartResponse }): JSX.Element => (
  <div className={styles.counts}>
    <span>대상 {response.totalCount}</span>
    <span>cancel 요청 {response.cancelRequestedCount}</span>
    <span>CANCELED 확인 {response.cancelledCount}</span>
    <span>재주입 주문 {response.restartedOrderCount}</span>
    {response.alreadyStartedOrderCount > 0 ? <span>앞 호출에서 이미 재주입 {response.alreadyStartedOrderCount}</span> : null}
    {Object.entries(response.otherStatusCounts).map(([key, count]) => (
      <Tag key={key} tone={key === 'NOT_CANCELLABLE' ? 'neutral' : 'warn'}>
        {key} {count}
        {STATUS_COUNT_LABEL[key] ? ` · ${STATUS_COUNT_LABEL[key]}` : ''}
      </Tag>
    ))}
  </div>
)

const NextSteps = ({ call, latest, c }: { call: RestartCall; latest: boolean; c: WorkflowRestartController }): JSX.Element | null => {
  const { response } = call
  if (!response) return null
  const steps = nextStepsOf(response, call.ask.kind === 'resume')
  const resumable = resumableTargetsOf(response)
  const resumeExhausted = c.nextResumeRound > MAX_RESUME_ROUNDS

  return (
    <Stack>
      {steps.includes('DONE') ? (
        <Callout tone="good">cancel 할 run 이 남지 않았어요. 남은 V2 run 은 대기 창이 닫힌 run 이라 끝까지 돌아요.</Callout>
      ) : null}
      {steps.includes('RESUME') ? (
        <Callout tone="warn">
          아직 끝나지 않았거나 일시 실패한 run {resumable.length}건이 있어요. 1분쯤 뒤 이어받기로 넘기세요.
          {resumeExhausted ? ` 이어받기를 ${MAX_RESUME_ROUNDS}번 했어요. 더 하지 말고 사람에게 넘기세요.` : ''}
          {latest && !resumeExhausted ? (
            <div style={{ marginTop: 8 }}>
              <Button size="sm" disabled={c.submitting} onClick={() => c.ask({ kind: 'resume', targets: resumable })}>
                이어받기 {c.nextResumeRound}회차 ({resumable.length}건)
              </Button>
            </div>
          ) : null}
        </Callout>
      ) : null}
      {steps.includes('MANUAL_CHECK') ? (
        <Callout tone="crit">
          대기 창이 닫힌 뒤 cancel 된 run 이 있어요. 자동으로 다시 넣지 않았어요. 아래 주문 id 의 SLS 할당·출고지시 상태를 확인한 뒤 수동 재발사
          여부를 정하세요.
        </Callout>
      ) : null}
      {steps.includes('CALL_AGAIN') && c.lastStartInput ? (
        <Callout>
          마무리로 같은 조건을 한 번 더 부르세요. cancel 요청 0 이 나오면 끝이에요. 세 번 불러도 0 이 안 되면 비율 반영을 의심하세요.
          {latest ? (
            <div style={{ marginTop: 8 }}>
              <Button size="sm" variant="line" disabled={c.submitting} onClick={() => c.ask({ kind: 'start', input: c.lastStartInput! })}>
                같은 조건으로 다시
              </Button>
            </div>
          ) : null}
        </Callout>
      ) : null}
    </Stack>
  )
}

const TargetTables = ({ response }: { response: WorkflowRestartResponse }): JSX.Element => (
  <>
    {response.pendingTargets.length > 0 ? (
      <>
        <SectionHeading level={3} title="아직 끝나지 않은 run" count={`${response.pendingTargets.length}건`} />
        <DataTable
          columns={[
            { key: 'workflowId', label: 'workflowId', mono: true },
            { key: 'runId', label: 'runId', mono: true },
            { key: 'type', label: '타입' },
          ]}
          rows={response.pendingTargets.map((t) => ({
            key: targetKey(t),
            cells: { workflowId: t.workflowId, runId: t.runId, type: typeLabelOf(t.workflowType) },
          }))}
          maxHeight={320}
          stickyHeader
        />
      </>
    ) : null}
    {response.restartFailures.length > 0 ? (
      <>
        <SectionHeading level={3} title="재주입 실패" count={`${response.restartFailures.length}건`} />
        <DataTable
          columns={[
            { key: 'kind', label: '구분' },
            { key: 'workflowId', label: 'workflowId', mono: true },
            { key: 'orders', label: '주문 (standard_order.id)' },
            { key: 'reason', label: '사유' },
          ]}
          rows={response.restartFailures.map((f, index) => ({
            key: `${targetKey(f.target)}-${index}`,
            cells: {
              kind: isClosedAfterWindow(f) ? <Tag tone="crit">사람 확인</Tag> : <Tag tone="warn">이어받기</Tag>,
              workflowId: f.target.workflowId,
              orders: <span className={styles.ids}>{f.itemIds.length > 0 ? `${f.itemIds.length}건 · ${f.itemIds.join(', ')}` : '-'}</span>,
              reason: f.reason,
            },
          }))}
          maxHeight={320}
          stickyHeader
        />
      </>
    ) : null}
    {response.restartWorkflowIds.length > 0 ? (
      <details>
        <summary>재발사 워크플로 {response.restartWorkflowIds.length}건 (Completed · accepted 확인용)</summary>
        <p className={styles.raw}>{response.restartWorkflowIds.join('\n')}</p>
      </details>
    ) : null}
  </>
)

const callTitleOf = (call: RestartCall): string =>
  call.ask.kind === 'start'
    ? `${call.seq}번째 호출 · 새로 시작 (${[call.request.workflowType && typeLabelOf(call.request.workflowType), call.request.workflowIdPrefix]
        .filter(Boolean)
        .join(' · ')})`
    : `${call.seq}번째 호출 · 이어받기 ${call.resumeRound}회차 (${call.ask.targets.length}건)`

const CallResult = ({ call, latest, c }: { call: RestartCall; latest: boolean; c: WorkflowRestartController }): JSX.Element => (
  <div className={styles.call}>
    <Stack>
      <SectionHeading level={3} title={callTitleOf(call)} />
      {call.error ? <Callout tone="crit">{call.error.message}</Callout> : null}
      {call.outcomeUnknown ? (
        <Callout tone="warn">
          응답 없이 끊겼어요. 서버는 끝까지 처리했을 수 있어요. 같은 조건으로 다시 부르면 안전해요. cancel 뒤 끝난 run 은 로그 [WORKFLOW-RESTART] 의
          pending·restartFailed 값을 이어받기 칸에 붙여 넣으세요.
        </Callout>
      ) : null}
      {call.response ? (
        <>
          <Summary response={call.response} />
          <NextSteps call={call} latest={latest} c={c} />
          <TargetTables response={call.response} />
        </>
      ) : null}
      <details>
        <summary>요청·응답 원문 (incident 기록용)</summary>
        <p className={styles.raw}>{JSON.stringify({ request: call.request, response: call.response }, null, 2)}</p>
      </details>
    </Stack>
  </div>
)

const describeAsk = (ask: RestartAsk): JSX.Element =>
  ask.kind === 'start' ? (
    <>
      <p style={{ margin: 0 }}>
        조건에 맞는 Running run 중 대기 창 안의 run 을 cancel 하고, CANCELED 가 확인되면 주문을 원래 주입 경로로 다시 넣어요. 새 run 의 V1/V2 는
        지금 롤아웃 비율이 정해요.
      </p>
      <ul className={styles.list}>
        <li>타입: {ask.input.workflowType ? `${typeLabelOf(ask.input.workflowType)} · ${ask.input.workflowType}` : '조건 없음'}</li>
        <li>workflowId 접두: {ask.input.workflowIdPrefix.trim() || '조건 없음'}</li>
        <li>대상이 {MAX_RESTART_TARGETS}건을 넘으면 아무것도 cancel 하지 않고 거절돼요.</li>
        <li>응답까지 최대 2분쯤 걸리고, 그동안 탭을 옮길 수 없어요.</li>
      </ul>
    </>
  ) : (
    <p style={{ margin: 0 }}>
      run {ask.targets.length}건을 cancel 하지 않고 종료만 확인해, CANCELED 인 run 의 주문을 다시 넣어요. 같은 run 을 여러 번 넘겨도 겹쳐 넣지
      않아요.
    </p>
  )

/**
 * 워크플로 restart(`s-fopt-restart`) + 결과(`s-fopt-restart-result`). V2 롤백 runbook 3장 3~6단계를 이 화면에서 돈다 —
 * 새로 시작, 응답 읽기, 이어받기, 같은 조건 재호출. 호출 중에만 셸 탭을 잠근다. 응답을 놓친 채 탭을 옮겼다 돌아와 같은
 * 판단을 다시 하는 일을 막는다. 호출 기록은 이 탭이 떠 있는 동안만 남는다.
 */
export const WorkflowRestartPanel = ({ controller }: { controller: WorkflowRestartController }): JSX.Element => {
  const c = controller

  return (
    <>
      <SectionHeading
        id="s-fopt-restart"
        level={2}
        title="워크플로 restart"
        badge={<SourceBadge tone="new">신규 · POST /workflow/restarts</SourceBadge>}
      />
      <Stack>
        <Callout tone="warn">
          <b>V2 롤백 순서</b>
          <ul className={styles.list}>
            <li>롤아웃 비율을 먼저 내린다. restart 를 먼저 하면 새 run 도 V2 로 열려요.</li>
            <li>대기 창이 닫힌 run 은 건드리지 않아요. 그 run 은 끝까지 돌아요.</li>
            <li>결과의 안내대로 이어받기와 같은 조건 재호출을 해 cancel 요청 0 을 확인해요.</li>
          </ul>
          <a href={RUNBOOK_URL} target="_blank" rel="noreferrer">
            런북 열기 (runbook-outbound-fopt-plus-order-v2-restart-rollback.md)
          </a>
        </Callout>
        <StartForm c={c} />
        <SectionHeading level={3} title="응답을 놓쳤을 때 이어받기" />
        <ResumeForm c={c} />
      </Stack>

      <SectionHeading id="s-fopt-restart-result" level={2} title="restart 결과" count={c.calls.length > 0 ? `${c.calls.length}회` : undefined} />
      {c.submitting ? <Callout>restart 중… 응답이 올 때까지 탭을 옮길 수 없어요.</Callout> : null}
      {c.calls.length === 0 && !c.submitting ? <Callout>아직 호출하지 않았어요.</Callout> : null}
      <Stack>
        {c.calls.map((call, index) => (
          <CallResult key={call.seq} call={call} latest={index === 0} c={c} />
        ))}
      </Stack>
      <Note>
        자동으로 다시 부르지 않아요. 호출 기록은 이 화면을 떠나면 사라지니 원문을 incident 기록에 붙이세요. <b>MOMS_ENG</b> 권한 필요.
      </Note>

      <ConfirmDialog
        open={c.pending !== null}
        title={c.pending?.kind === 'resume' ? '이어받을까요?' : 'restart 할까요?'}
        body={c.pending ? describeAsk(c.pending) : null}
        confirmLabel={c.pending?.kind === 'resume' ? '이어받기' : 'restart'}
        danger
        onConfirm={c.confirm}
        onCancel={c.cancel}
      />
    </>
  )
}
